/**
 * Typed endpoint functions for `/api/game-sessions/*`
 * (docs/APP_PLAN.md §8.1, §8.3, §12 M3). `config` and `action` are
 * deliberately `unknown` on the way in and `view` is `unknown` on the way
 * out: this package is generic across every registered game, and each
 * game's own web UI (`apps/web/src/games/<game-id>`) is responsible for
 * shaping/interpreting its own config and view payloads.
 *
 * Every mutating call generates its own client-side `Idempotency-Key`
 * (docs/APP_PLAN.md §8.3) — a fresh UUID per explicit user action, so a
 * genuine accidental double-submission is still caught by the server's
 * optimistic-concurrency version guard, while a network-level retry of the
 * *same* logical request (handled by a caller that chooses to retry with
 * the same generated body) replays the original response instead of acting
 * twice.
 */
import type { HttpClient } from './http-client.js';
import { buildQueryString } from './query.js';
import type { GameSession, PaginationParams, SessionSummary } from './types.js';

interface GameSessionEnvelope {
  session: GameSession;
}

interface SessionSummaryListEnvelope {
  sessions: SessionSummary[];
}

/** The two independent concurrency slots a session can occupy (APP_SPEC §21.1). */
export type SessionSlot = 'solo' | 'multiplayer';

export interface AbandonSessionResult {
  sessionId: string;
  slot: SessionSlot;
  /** `forfeited` for an active session, `cancelled` for a pending one that never started. */
  outcome: 'forfeited' | 'cancelled';
}

/**
 * The payload carried in `ApiError.details.conflict` when a request is
 * rejected with `ACTIVE_SESSION_EXISTS` (APP_SPEC §21.1) — enough for a
 * client to name the blocking session and offer resume-or-abandon instead
 * of a bare error. Never includes game state.
 */
export interface ActiveSessionConflict {
  sessionId: string;
  gameId: string;
  gameName: string;
  mode: string;
  status: string;
  slot: SessionSlot;
  /** True when abandoning would forfeit to real opponents rather than discard a private run. */
  hasOpponents: boolean;
}

export interface CreateSoloSessionInput {
  gameId: string;
  /** Already game-validated on the server; the client sends whatever the game's config form collected. */
  config?: unknown;
}

/**
 * `mode` is any platform mode the target game declares in its own
 * `supportedModes` other than `'solo'` (docs/APP_PLAN.md §8.1) — this
 * client never hardcodes a specific mode or game id (CLAUDE.md §2).
 */
export interface CreateMultiplayerSessionInput {
  gameId: string;
  mode: string;
  config?: unknown;
  opponentUsername: string;
}

/**
 * `mode` is any platform mode the target game declares in its own
 * `supportedModes` (docs/games/word-rush/GAME_PLAN.md §13.2) — only
 * meaningful for a game that declares `joinModel: 'open_lobby'` in its
 * metadata; the server rejects this shape for any other game. `invitees`
 * is capped at 9 usernames server-side.
 */
export interface CreateLobbySessionInput {
  gameId: string;
  mode: string;
  config?: unknown;
  inviteeUsernames?: string[];
}

export interface JoinSessionByCodeInput {
  joinCode: string;
}

export interface ListSessionsInput extends PaginationParams {
  status: 'active' | 'completed';
}

export interface SubmitGameActionInput {
  expectedVersion: number;
  action: unknown;
}

/** A cache-invalidation ping — an integer version and a timestamp, never a payload (docs/games/word-delve/GAME_PLAN.md §15.1). */
export interface SessionRevisionFrame {
  version: number;
  at: number;
}

/** Cell indices and a seat number only — never letters, HP, or any other hidden value. */
export interface SessionPresenceFrame {
  seat: number;
  cells: number[];
  at: number;
}

export interface SessionStreamHandlers {
  onRevision?(frame: SessionRevisionFrame): void;
  onPresence?(frame: SessionPresenceFrame): void;
  /** Fires once the connection is established (including after a browser-managed reconnect). */
  onOpen?(): void;
  /** Fires on any connection failure, including the ones the browser will silently retry. */
  onError?(event: Event): void;
}

export interface SubscribeToSessionOptions {
  /** Overrides the `EventSource` constructor — used by tests; defaults to the global `EventSource`. */
  eventSourceImpl?: typeof EventSource;
}

export interface GameSessionsApi {
  /** `POST /api/game-sessions` with `mode: 'solo'`. Creates and immediately runs `createGame`. */
  createSolo(input: CreateSoloSessionInput): Promise<GameSession>;
  /**
   * `POST /api/game-sessions` with a non-solo mode and a named opponent.
   * Creates a `pending` session plus its invitation (docs/APP_PLAN.md
   * §8.1); `createGame` does not run until the invitee accepts.
   */
  createMultiplayer(input: CreateMultiplayerSessionInput): Promise<GameSession>;
  /**
   * `POST /api/game-sessions` with `joinModel: 'open_lobby'` semantics
   * (docs/games/word-rush/GAME_PLAN.md §13.2, W1). Creates an `active`
   * session immediately with only the host seated, mints a join code, and
   * sends an invitation + notification to each named invitee.
   */
  createLobby(input: CreateLobbySessionInput): Promise<GameSession>;
  /**
   * `POST /api/game-sessions/join` (docs/games/word-rush/GAME_PLAN.md
   * §13.2, W3). Rejects with `NOT_FOUND` for an invalid, closed, full, or
   * block-affected code — deliberately indistinguishable reasons (D7).
   */
  joinByCode(input: JoinSessionByCodeInput): Promise<GameSession>;
  /** `GET /api/game-sessions/:id`. Returns the caller's player-specific view; 404 for a non-participant. */
  get(sessionId: string): Promise<GameSession>;
  /** `GET /api/game-sessions?status=...`. Lightweight summaries for the dashboard's active/completed games sections (APP_SPEC §18). */
  list(input: ListSessionsInput): Promise<SessionSummary[]>;
  /** `POST /api/game-sessions/:id/actions`. May reject with `STALE_STATE` (`ApiError.details` carries a fresh `GameSession`). */
  submitAction(sessionId: string, input: SubmitGameActionInput): Promise<GameSession>;
  /**
   * `POST /api/game-sessions/:id/forfeit` (docs/APP_PLAN.md §8.6, §12 M6).
   * The single player-initiated forfeit/resign entry point: in multiplayer
   * the caller loses and the opponent wins; in solo the session is recorded
   * as abandoned. Rejects with `SESSION_NOT_ACTIVE` if the session is not
   * currently `active` (already completed, or still `pending`).
   */
  forfeit(sessionId: string): Promise<GameSession>;
  /**
   * `POST /api/game-sessions/:id/abandon` (APP_SPEC §21.1). Gives up the
   * session occupying a concurrency slot so the caller can start or join
   * another game of the same kind. The server decides how: an `active`
   * session is forfeited (opponents win, statistics reflect it), a
   * `pending` one is cancelled along with its outstanding invitations.
   */
  abandon(sessionId: string): Promise<AbandonSessionResult>;
  /**
   * `GET /api/game-sessions/:id/stream` (docs/games/word-delve/GAME_PLAN.md
   * §15.1) — only exists for a game declaring `metadata.realtime`; 404s
   * otherwise, surfaced as an `onError` call rather than a thrown promise
   * rejection (an `EventSource` never rejects a promise). Returns an
   * `unsubscribe` function that closes the underlying connection. Carries
   * no authoritative payload: `onRevision` is a cache-invalidation ping,
   * `onPresence` is cosmetic cursor/path data.
   */
  subscribeToSession(
    sessionId: string,
    handlers: SessionStreamHandlers,
    options?: SubscribeToSessionOptions,
  ): () => void;
  /**
   * `POST /api/game-sessions/:id/presence` (docs/games/word-delve/
   * GAME_PLAN.md §15.1) — cosmetic, never-persisted cursor/path fan-out, at
   * most 15 cell indices. No `Idempotency-Key`: unlike `submitAction`, this
   * never mutates authoritative state, so there is nothing for a retry to
   * duplicate.
   */
  postPresence(sessionId: string, cells: number[]): Promise<void>;
}

function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export function createGameSessionsApi(http: HttpClient): GameSessionsApi {
  return {
    async createSolo(input) {
      const { session } = await http.request<GameSessionEnvelope>({
        method: 'POST',
        path: '/game-sessions',
        body: { gameId: input.gameId, mode: 'solo', config: input.config },
        headers: { 'Idempotency-Key': newIdempotencyKey() },
      });
      return session;
    },

    async createMultiplayer(input) {
      const { session } = await http.request<GameSessionEnvelope>({
        method: 'POST',
        path: '/game-sessions',
        body: {
          gameId: input.gameId,
          mode: input.mode,
          config: input.config,
          opponent: { username: input.opponentUsername },
        },
        headers: { 'Idempotency-Key': newIdempotencyKey() },
      });
      return session;
    },

    async createLobby(input) {
      const { session } = await http.request<GameSessionEnvelope>({
        method: 'POST',
        path: '/game-sessions',
        body: {
          gameId: input.gameId,
          mode: input.mode,
          config: input.config,
          invitees: input.inviteeUsernames?.map((username) => ({ username })),
        },
        headers: { 'Idempotency-Key': newIdempotencyKey() },
      });
      return session;
    },

    async joinByCode(input) {
      const { session } = await http.request<GameSessionEnvelope>({
        method: 'POST',
        path: '/game-sessions/join',
        body: { joinCode: input.joinCode },
        headers: { 'Idempotency-Key': newIdempotencyKey() },
      });
      return session;
    },

    async get(sessionId) {
      const { session } = await http.request<GameSessionEnvelope>({
        method: 'GET',
        path: `/game-sessions/${encodeURIComponent(sessionId)}`,
      });
      return session;
    },

    async list(input) {
      const { sessions } = await http.request<SessionSummaryListEnvelope>({
        method: 'GET',
        path: `/game-sessions${buildQueryString({
          status: input.status,
          limit: input.limit,
          offset: input.offset,
        })}`,
      });
      return sessions;
    },

    async submitAction(sessionId, input) {
      const { session } = await http.request<GameSessionEnvelope>({
        method: 'POST',
        path: `/game-sessions/${encodeURIComponent(sessionId)}/actions`,
        body: { expectedVersion: input.expectedVersion, action: input.action },
        headers: { 'Idempotency-Key': newIdempotencyKey() },
      });
      return session;
    },

    async forfeit(sessionId) {
      const { session } = await http.request<GameSessionEnvelope>({
        method: 'POST',
        path: `/game-sessions/${encodeURIComponent(sessionId)}/forfeit`,
        headers: { 'Idempotency-Key': newIdempotencyKey() },
      });
      return session;
    },

    async abandon(sessionId) {
      return http.request<AbandonSessionResult>({
        method: 'POST',
        path: `/game-sessions/${encodeURIComponent(sessionId)}/abandon`,
        headers: { 'Idempotency-Key': newIdempotencyKey() },
      });
    },

    subscribeToSession(sessionId, handlers, options = {}) {
      const EventSourceImpl =
        options.eventSourceImpl ?? (globalThis as { EventSource?: typeof EventSource }).EventSource;
      if (!EventSourceImpl) {
        throw new Error(
          'EventSource is not available in this environment; pass options.eventSourceImpl.',
        );
      }

      // Cookie-based sessions (docs/APP_PLAN.md §9.1) travel automatically
      // on a same-origin `EventSource` request -- `withCredentials` matters
      // only for a cross-origin one, but is harmless to set unconditionally
      // (`EventSource` cannot set the CSRF client header this package's
      // mutating `fetch` calls use, but a GET needs none: `plugins/
      // security.ts`'s CSRF check only applies to mutating methods).
      const url = `${http.getBaseUrl()}/game-sessions/${encodeURIComponent(sessionId)}/stream`;
      const source = new EventSourceImpl(url, { withCredentials: true });

      source.addEventListener('open', () => {
        handlers.onOpen?.();
      });
      source.addEventListener('revision', (event) => {
        try {
          const data = JSON.parse((event as MessageEvent<string>).data) as SessionRevisionFrame;
          handlers.onRevision?.(data);
        } catch {
          // A malformed frame is ignored -- the caller's own poll/refetch
          // path is always the source of truth regardless.
        }
      });
      source.addEventListener('presence', (event) => {
        try {
          const data = JSON.parse((event as MessageEvent<string>).data) as SessionPresenceFrame;
          handlers.onPresence?.(data);
        } catch {
          // Ignored -- presence is cosmetic.
        }
      });
      source.addEventListener('error', (event) => {
        handlers.onError?.(event);
      });

      return () => {
        source.close();
      };
    },

    async postPresence(sessionId, cells) {
      await http.requestMaybeEmpty<undefined>({
        method: 'POST',
        path: `/game-sessions/${encodeURIComponent(sessionId)}/presence`,
        body: { cells },
      });
    },
  };
}
